import type { Routes } from '@angular/router';
import { orgSettingsRoutes, orgTeamEntryRoute, schoolbellCallbackRoute, spinoffAuthGuard, loadSpinoffLoginRoute } from '@sbe-spinoff-client/spinoff-platform';

export const appRoutes: Routes = [
  // 서비스 소개 랜딩 — guard 없음, 인증 여부와 무관하게 항상 렌더되고 "입장하기" 버튼
  // 클릭으로만 /dashboard로 이동한다(자동 리디렉션 아님, 2026-08-13).
  // shellMode: 'toolbar' — 헤더(브랜드 바)는 라우트 불문 항상 노출한다(사용자 지시, 2026-08-12).
  // 미인증 상태라 showToolbarChrome()이 false로 평가돼 조직 스위처·알림 종·프로필 메뉴 등
  // 인증 후 전용 슬롯은 그려지지 않고 브랜드 바만 보인다.
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/home/home.component').then((m) => m.HomeComponent),
    data: { shellMode: 'toolbar' },
    title: '학교·단체 문자 발송 서비스',
  },
  // 로그인 전용 — guard 없음. shellMode: 'toolbar' — 위 랜딩과 같은 이유로 헤더 항상 노출.
  {
    path: 'login',
    loadComponent: loadSpinoffLoginRoute,
    data: { shellMode: 'toolbar' },
    title: '로그인',
  },
  // 학교종이(schoolbell-e) OIDC 로그인 콜백(RP-initiated, "학교종이로 로그인" 버튼) —
  // schoolbell-e AS가 브라우저를 여기로 돌려보낸다(redirect_uri가 이 웹사이트 origin). code+state를
  // sbe-sms-api로 POST 포워딩한다.
  // shellBare: true — 팝업/리다이렉트 표면이라 셸 자체를 건너뛴다(footer도 없음). "헤더 항상
  // 노출" 정책(위 랜딩/로그인 참고)의 예외로 남긴다 — 이 화면은 code 교환이 끝나는 즉시
  // navigateByUrl로 즉시 이탈하는 처리 중 화면일 뿐 사용자가 머무는 페이지가 아니고,
  // shellBare가 header를 강제로 'none'으로 덮는 동작은 outlet 단일화 회피 장치와 같은
  // 코드 경로(libs/client/app-shell/src/lib/app-shell-root.component.html)에 묶여 있어
  // 이 앱 범위에서 셸 라이브러리를 수정하지 않는 이상 분리할 수 없다.
  schoolbellCallbackRoute({ returnPath: '/dashboard', shellBare: true }),
  // (Option A) schoolbell-e magic-link 핸드오프 진입(provider-initiated push,
  // 2026-07-09-sms-magic-link-handoff.md) — 위 RP-initiated 로그인과는 별개 진입 시나리오, guard 없음.
  // shellBare: true — 위 schoolbell 콜백과 동일한 이유(처리 중 즉시 이탈, "헤더 항상 노출"
  // 정책 예외)로 유지한다.
  {
    path: 'auth/erps/:erpId/callback',
    loadComponent: () =>
      import('@sbe-spinoff-client/schoolbell-org-link').then((m) => m.ErpCallbackComponent),
    data: { shellBare: true },
  },
  // NICE CheckPlus 본인인증 콜백 — 팝업에서 열림, guard 불필요. shellBare: true — window.opener로
  // postMessage 후 즉시 window.close()하는 팝업 전용 화면(브랜드 헤더가 그려질 시간조차 없다) —
  // "헤더 항상 노출" 정책 예외로 유지한다.
  {
    path: 'nice-cp/result',
    loadComponent: () =>
      import('./features/nice-cp-result/nice-cp-result.component').then(
        (m) => m.NiceCpResultComponent,
      ),
    data: { shellBare: true },
  },
  // 로그아웃 후 도착 — 홈으로 리다이렉트
  { path: 'signed-out', redirectTo: '', pathMatch: 'full' },
  // 인증 영역 — spinoffAuthGuard 보호, 미인증 시 /login 으로 리디렉션. 셸(App 루트)에 컴포넌트
  // 없이 라우트 그룹으로만 존재 — 자식이 App의 <router-outlet>에 직접 그려진다
  // (2026-08-10 layout/shell/app-shell.component.ts 삭제, 셸을 루트로 승격).
  // data.appShell — SpinoffActiveOrgContextService가 "인증 영역 안쪽"을 판별하는 마커. 로그아웃/랜딩/로그인처럼
  // 이 영역 밖으로 나가는 라우트는 이 마커가 없으므로 org 컨텍스트가 항상 리셋된다.
  {
    path: '',
    canActivate: [spinoffAuthGuard],
    data: { appShell: true, shellMode: 'toolbar' },
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: 'credits',
        loadComponent: () =>
          import('./features/credits/credits.component').then(
            (m) => m.CreditsComponent,
          ),
        title: '크레딧',
      },
      // orgQueryParam: true — PG 리다이렉트 복귀 지점. 전체 페이지 이동이라 in-memory org
      // 컨텍스트가 유실되므로, returnUrl에 실어 보낸 `?orgId=` 쿼리 파라미터로 복원한다
      // (spinoff-active-org-context.service.ts, libs/client/spinoff-platform).
      {
        path: 'credits/result',
        loadComponent: () =>
          import('./features/credits/credit-result.component').then(
            (m) => m.CreditResultComponent,
          ),
        data: { orgQueryParam: true },
      },
      // 개인 컨텍스트는 문자를 보낼 수 없다(컨텍스트 스위처·사이드바에서 이미 숨김,
      // 2026-08-28) — 아래 발송/이력/설정 계열 개인 경로(구 프로덕션 북마크·딥링크 포함)는
      // 전부 대시보드로 되돌린다. org-scoped 버전(`orgs/:orgId/send` 등, 아래 children)은
      // 그대로 유지한다.
      { path: 'send', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'history', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'history/:messageId', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'settings', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'settings/originators/new', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'settings/templates', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        // orgs/:orgId(org-scoped 트리)와 접두사가 겹쳐 마치 그 아래에 있는 것처럼 보이는 걸
        // 피하려고 my-orgs로 둔다(2026-08-14) — "내 단체 목록"이라는 개인 계정 범위 화면임을
        // path 자체로 드러낸다.
        path: 'my-orgs',
        loadComponent: () =>
          import('./features/orgs/orgs.component').then((m) => m.OrgsComponent),
      },
      {
        // 같은 이유로 my-org-invitations — "단체 목록" 아래에 얹혀 있던 orgs/invitations에서
        // 분리(2026-08-14) — 단체 선택과 무관한 별개 화면이라 org 목록 URL 하위처럼 보이면
        // 안 된다.
        path: 'my-org-invitations',
        loadComponent: () =>
          import('./features/org-invitations/org-invitations.component').then(
            (m) => m.OrgInvitationsComponent,
          ),
      },
      // 통합 인박스(2026-08-06) — 개인/org 컨텍스트 구분 없이 항상 같은 목록이라
      // orgs/:orgId 하위 org 버전이 없다.
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notifications/notifications.component').then(
            (m) => m.NotificationsComponent,
          ),
      },
      // 구 경로(my-orgs/my-org-invitations로 리네임되기 전, 2026-08-14 이전 프로덕션에 이미
      // 배포된 경로) 북마크·딥링크 호환용 리다이렉트 — 'orgs/:orgId'보다 먼저 와야 'orgs',
      // 'orgs/invitations'가 :orgId 파라미터로 잘못 매칭되지 않는다(code-review 지적).
      { path: 'orgs', redirectTo: 'my-orgs', pathMatch: 'full' },
      { path: 'orgs/invitations', redirectTo: 'my-org-invitations', pathMatch: 'full' },
      {
        // org 컨텍스트(단체 설정) 라우트 트리 — 사이드바는 로컬 셸이 아니라 app.ts의
        // navItems()가 채우는 전역 app-shell 사이드바다(2026-08-29, ShellNavSection
        // 도입으로 로컬 2차 셸 OrgSettingsShellComponent를 걷어내고 평탄화). 이 라우트
        // 자체는 컴포넌트가 없다 — 위 `path: ''` 그룹과 동일하게 순수 경로 프리픽스 +
        // children 전용.
        path: 'orgs/:orgId',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/org-detail/org-detail.component').then(
                (m) => m.OrgDetailComponent,
              ),
            title: '멤버 관리',
          },
          // 학교종이 팀 단위 입장(`orgs/:orgId/teams/:teamId`, 백엔드 API 경로와 같은 모양) — sms는 팀 전용 화면이 없어 단체 기본 화면(멤버 관리)을 그대로 보여 준다.
          orgTeamEntryRoute({
            loadComponent: () =>
              import('./features/org-detail/org-detail.component').then(
                (m) => m.OrgDetailComponent,
              ),
            title: '멤버 관리',
          }),
          {
            path: 'send',
            loadComponent: () =>
              import('./features/send/send.component').then(
                (m) => m.SendComponent,
              ),
          },
          // 앱 간에 공유하는 단체 설정 화면(spinoff-platform lib) — 문서 제목은 기존과 같이 '변경 이력'.
          // `teams`: 학급구조(`teams`)와 학급별 학생 명단(`teams/:teamId/members`). sms는 학급 생성·이름변경·삭제·이동과, 대상이 이 서비스에 로그인하지 않는 문자 발송용 기록 전용(record-only)
          // 학생 추가·삭제를 켠다(apps/sbe-sms/plans/archive/2026-08-28-org-team-roster-record-only-members.md). 백엔드는 spinoff-platform 공유 OrgTeam을 그대로 쓴다.
          ...orgSettingsRoutes({
            auditLogTitle: '변경 이력',
            membersAdd: true,
            labels: { title: '멤버 분류' },
            teams: { structureTitle: '학급구조', rosterTitle: '학생 명단', structureEditable: true, rosterAddMember: true },
          }),
          {
            path: 'history',
            loadComponent: () =>
              import('./features/history/history.component').then(
                (m) => m.HistoryComponent,
              ),
          },
          {
            path: 'history/:messageId',
            loadComponent: () =>
              import('./features/history-detail/history-detail.component').then(
                (m) => m.HistoryDetailComponent,
              ),
          },
          {
            path: 'credits',
            loadComponent: () =>
              import('./features/credits/credits.component').then(
                (m) => m.CreditsComponent,
              ),
          },
          // 단체 포인트 팀원 분배·회수 — 'credits'(잔액/구매/이용내역, CreditsComponent)와
          // 경로가 겹치지 않도록 하위 경로로 분리. 백엔드 orgs/:orgId/credits/members 와 정렬.
          {
            path: 'credits/members',
            loadComponent: () =>
              import(
                './features/org-credits/org-credit-distribution.component'
              ).then((m) => m.OrgCreditDistributionComponent),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./features/settings/settings.component').then(
                (m) => m.SettingsComponent,
              ),
          },
          {
            path: 'settings/originators/new',
            loadComponent: () =>
              import(
                './features/settings/originator-new-page/originator-new-page.component'
              ).then((m) => m.OriginatorNewPageComponent),
          },
          {
            path: 'settings/templates',
            loadComponent: () =>
              import('./features/templates/templates.component').then(
                (m) => m.TemplatesComponent,
              ),
          },
        ],
      },
    ],
  },
];
